import React from 'react';
import { useNavigate } from 'react-router-dom';
import './Legal.css';

const Privacy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="legal-page">
      <div className="legal-container">
        <button className="back-btn" onClick={() => navigate('/')}>
          ← Back to Home
        </button>

        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: November 22, 2025</p>

        <section className="legal-section">
          <h2>1. Introduction</h2>
          <p>
            Welcome to Tiny Little Fox Studio ("we," "our," or "us"). We respect your privacy and are committed to protecting your personal data. 
            This privacy policy will inform you about how we look after your personal data when you visit our website and use our services, 
            and tell you about your privacy rights and how the law protects you.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Information We Collect</h2>
          <h3>2.1 Personal Information</h3>
          <p>We may collect the following types of personal information:</p>
          <ul>
            <li><strong>Account Information:</strong> Email address, username, and password when you register</li>
            <li><strong>Profile Information:</strong> Any additional information you choose to provide</li>
            <li><strong>Payment Information:</strong> Payment details processed through secure third-party payment processors</li>
            <li><strong>Usage Data:</strong> Information about how you use our services, including storybooks created, AI generations requested</li>
          </ul>

          <h3>2.2 Automatically Collected Information</h3>
          <ul>
            <li><strong>Device Information:</strong> IP address, browser type, operating system</li>
            <li><strong>Cookies and Similar Technologies:</strong> We use cookies to enhance your experience</li>
            <li><strong>Log Data:</strong> Server logs including access times, pages viewed, and other system activity</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. How We Use Your Information</h2>
          <p>We use your information for the following purposes:</p>
          <ul>
            <li>To provide and maintain our services</li>
            <li>To process your transactions and manage your account</li>
            <li>To generate AI-powered illustrations and story content</li>
            <li>To improve and optimize our services</li>
            <li>To communicate with you about updates, support, and promotional offers</li>
            <li>To detect, prevent, and address technical issues or fraud</li>
            <li>To comply with legal obligations</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. AI-Generated Content</h2>
          <p>
            When you use our AI-powered features to generate illustrations or text, we may process your prompts and descriptions 
            through third-party AI services (such as OpenAI and Google). Your content and prompts may be processed by these services 
            according to their respective privacy policies. We recommend reviewing:
          </p>
          <ul>
            <li>OpenAI Privacy Policy: <a href="https://openai.com/privacy" target="_blank" rel="noopener noreferrer">https://openai.com/privacy</a></li>
            <li>Google Privacy Policy: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a></li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>5. Data Sharing and Disclosure</h2>
          <p>We may share your information in the following circumstances:</p>
          <ul>
            <li><strong>Service Providers:</strong> With third-party vendors who help us operate our services (AI providers, payment processors, hosting services)</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, sale, or acquisition of our business</li>
            <li><strong>With Your Consent:</strong> When you explicitly agree to share your information</li>
          </ul>
          <p>
            <strong>We do not sell your personal information to third parties.</strong>
          </p>
        </section>

        <section className="legal-section">
          <h2>6. Your Content Ownership</h2>
          <p>
            You retain full ownership and copyright of the storybooks and content you create using our platform. 
            We do not claim any ownership rights to your created works. However, by using our service, you grant us a 
            limited license to store, display, and process your content solely for the purpose of providing our services to you.
          </p>
        </section>

        <section className="legal-section">
          <h2>7. Data Security</h2>
          <p>
            We implement appropriate technical and organizational security measures to protect your personal data against 
            unauthorized access, alteration, disclosure, or destruction. These measures include:
          </p>
          <ul>
            <li>Encryption of data in transit and at rest</li>
            <li>Secure authentication and access controls</li>
            <li>Regular security assessments and updates</li>
            <li>Secure cloud infrastructure (Google Cloud Platform)</li>
          </ul>
          <p>
            However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Data Retention</h2>
          <p>
            We retain your personal information for as long as your account is active or as needed to provide you services. 
            You may request deletion of your account and associated data at any time. We will retain and use your information 
            as necessary to comply with legal obligations, resolve disputes, and enforce our agreements.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. Your Privacy Rights</h2>
          <p>Depending on your location, you may have the following rights:</p>
          <ul>
            <li><strong>Access:</strong> Request access to your personal data</li>
            <li><strong>Correction:</strong> Request correction of inaccurate data</li>
            <li><strong>Deletion:</strong> Request deletion of your personal data</li>
            <li><strong>Data Portability:</strong> Request a copy of your data in a portable format</li>
            <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing where applicable</li>
            <li><strong>Opt-Out:</strong> Opt-out of marketing communications</li>
          </ul>
          <p>
            To exercise these rights, please contact us at the email address provided in the Contact section below.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. Children's Privacy</h2>
          <p>
            Our services are not intended for children under 13 years of age. We do not knowingly collect personal information 
            from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, 
            please contact us so we can delete such information.
          </p>
        </section>

        <section className="legal-section">
          <h2>11. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your country of residence. 
            These countries may have different data protection laws. By using our services, you consent to such transfers.
          </p>
        </section>

        <section className="legal-section">
          <h2>12. Cookies and Tracking Technologies</h2>
          <p>
            We use cookies and similar tracking technologies to enhance your experience. You can control cookies through your 
            browser settings, but disabling cookies may affect the functionality of our services.
          </p>
        </section>

        <section className="legal-section">
          <h2>13. Third-Party Links</h2>
          <p>
            Our website may contain links to third-party websites (such as Amazon KDP). We are not responsible for the privacy 
            practices of these external sites. We encourage you to review their privacy policies.
          </p>
        </section>

        <section className="legal-section">
          <h2>14. Changes to This Privacy Policy</h2>
          <p>
            We may update this privacy policy from time to time. We will notify you of any changes by posting the new privacy policy 
            on this page and updating the "Last Updated" date. You are advised to review this policy periodically for any changes.
          </p>
        </section>

        <section className="legal-section">
          <h2>15. Contact Us</h2>
          <p>
            If you have any questions about this privacy policy or our data practices, please contact us at:
          </p>
          <p className="contact-info">
            <strong>Email:</strong> privacy@tinylittlefox.com<br />
            <strong>Website:</strong> https://tinylittlefox.com
          </p>
        </section>

        <div className="legal-footer">
          <p>
            By using Tiny Little Fox Studio, you acknowledge that you have read and understood this Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
